import React, { useContext, useEffect, useState } from 'react';
import { Avatar, Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Slide } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import PaletteIcon from '@mui/icons-material/Palette';
import LanguageIcon from '@mui/icons-material/Language';
import InfoIcon from '@mui/icons-material/Info';
import PolicyIcon from '@mui/icons-material/Policy';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useTranslation } from "react-i18next";
import { logoutUser } from '../UserLogin/AuthSignIn';
import { setChannel } from '../../store/reducers/channel.reducer';
import { getUser, setUser } from '../../store/reducers/user.reducer';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Block, Brightness4, Brightness7, Language, LogoutRounded, Settings } from '@mui/icons-material';
import { ColorModeContext } from '../../components/App/App';
import AboutUsDialog from '../AboutUsDialog/AboutUsDialog';
import { PAGE_ROUTES, PRIVACY_POLICY_URL, ROUTES_PATH } from '../../utils/constants';
import { useAppSelector } from '../../store/hooks';

interface User {
  profilePic?: string;
  username?: string;
  email?: string;
}

interface MobileProfilePageProps {
  user?: User;
}

const Transition = React.forwardRef(function Transition(props: any, ref) {
  return <Slide direction="up" ref={ref} {...props}>{props.children}</Slide>;
});

const MobileProfilePage: React.FC<MobileProfilePageProps> = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const [openLang, setOpenLang] = useState(false);
  const [openTheme, setOpenTheme] = useState(false);
  const [openAboutUs, setOpenAboutUs] = useState(false);
  const colorMode = useContext(ColorModeContext) as any;

  let user = useAppSelector((state) => getUser(state));

  useEffect(() => {
    if (!user?.userID) {
      navigate(ROUTES_PATH.LOGIN, {
        state: {
          redirectTo: PAGE_ROUTES.MORE_OPTIONS_PAGE,
        },
      });
    }
  }, [user]);

  const changeTheme = (mode: "light" | "dark" | "system") => {
    colorMode.setMode(mode);
    setOpenTheme(false);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("appLanguage", lng);
    setOpenLang(false);
  };

  const openPrivacyPolicy = (url: string) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleLogoutConfirmation = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    try {
      const userID = localStorage.getItem("userID");
      const videoID = "";
      const response = await logoutUser(userID, videoID);

      console.log("Logout successful:", response);

      // Clear token and user info
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("persist:root")
      dispatch(setChannel(null));
      dispatch(setUser(null));
      navigate("/login");
      navigate(0);
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Logout failed. Please try again.");
    }
    setLogoutDialogOpen(false);
  };


  const handleEditProfile = () => {
    navigate("/profile");
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Profile Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          bgcolor: "background.paper",
        }}
      >
        <Avatar src={user?.profilePic} sx={{ width: 64, height: 64, mr: 2 }}>
          {user?.username?.[0] || <AccountCircleIcon />}
        </Avatar>
        <Box>
          <Typography variant="h6">{user?.username}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`${user?.firstName || ""} ${user?.lastName || ""}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />
      <List>
        <ListItemButton onClick={handleEditProfile}>
          <ListItemIcon>
            <AccountCircleIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("account")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("userProfile.editProfile")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => setOpenLang(true)}>
          <ListItemIcon>
            <LanguageIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.appLanguage")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.appLanguageSubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => setOpenTheme(true)}>
          <ListItemIcon>
            <PaletteIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.theme")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.themeSubtext")}
            </Typography>
          </Box>
        </ListItemButton>


        <ListItemButton onClick={() => navigate("/subscriptions")}>
          <ListItemIcon>
            <SubscriptionsIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.subscriptions")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.subscriptionsSubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => navigate("/blocked-channels")}>
          <ListItemIcon>
            <Block />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.blockedChannels")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.blockedChannelsSubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => navigate("/blocked-videos")}>
          <ListItemIcon>
            <Block />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.blockedVideos")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.blockedVideosSubtext")}
            </Typography>
          </Box>
        </ListItemButton>


        <ListItemButton onClick={() => setOpenAboutUs(true)}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.aboutUs")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.aboutUsSubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => openPrivacyPolicy(PRIVACY_POLICY_URL)}>
          <ListItemIcon>
            <PolicyIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.privacyPolicy")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.privacyPolicySubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={() => navigate("/contact-us")}>
          <ListItemIcon>
            <ContactMailIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">{t("nav.contactUs")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("nav.contactUsSubtext")}
            </Typography>
          </Box>
        </ListItemButton>

        <ListItemButton onClick={handleLogoutConfirmation}>
          <ListItemIcon><LogoutRounded /></ListItemIcon>
          <ListItemText
            primary={t("logoutDialog.logout")}
            secondary={t("logoutDialog.logoutSubtext")}
            sx={{ color: 'text.secondary' }}
          />
        </ListItemButton>

        <ListItemButton onClick={() => navigate("/delete-account")}>
          <ListItemIcon>
            <DeleteForeverIcon />
          </ListItemIcon>
          <Box>
            <Typography variant="body2">
              {t("deleteDialog.deleteAccount")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("deleteDialog.deleteAccountSubtext")}
            </Typography>
          </Box>
        </ListItemButton>
      </List>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>{t("logoutDialog.logoutConfirmLabel")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("logoutDialog.logoutConfirmation")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} color="primary">{t("logoutDialog.cancel")}</Button>
          <Button onClick={confirmLogout} color="primary">{t("logoutDialog.logoutConfirm")}</Button>
        </DialogActions>
      </Dialog>

      {/* Bottom Sheet for Language Selection */}
      <Dialog
        open={openLang}
        TransitionComponent={Transition}
        onClose={() => setOpenLang(false)}
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            bottom: 0,
            m: 0,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            width: "100%",
            maxWidth: "100%",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{t("selectLanguage")}</Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton onClick={() => changeLanguage("en")}>
            <ListItemIcon>
              <Language />
            </ListItemIcon>
            <Typography>{t("english")}</Typography>
          </ListItemButton>
          <ListItemButton onClick={() => changeLanguage("hi")}>
            <ListItemIcon>
              <Language />
            </ListItemIcon>
            <Typography>हिन्दी</Typography>
          </ListItemButton>
        </List>
      </Dialog>


      <Dialog
        open={openTheme}
        TransitionComponent={Transition}
        onClose={() => setOpenTheme(false)}
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            m: 0,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            width: "100%",
            maxWidth: "100%",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{t("chooseTheme")}</Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton onClick={() => changeTheme("light")}>
            <ListItemIcon>
              <Brightness7 />
            </ListItemIcon>
            <Typography>{t("themeOptions.light")}</Typography>
          </ListItemButton>

          <ListItemButton onClick={() => changeTheme("dark")}>
            <ListItemIcon>
              <Brightness4 />
            </ListItemIcon>
            <Typography>{t("themeOptions.dark")}</Typography>
          </ListItemButton>

          <ListItemButton onClick={() => changeTheme("system")}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <Typography>{t("themeOptions.system")}</Typography>
          </ListItemButton>
        </List>
      </Dialog>

      <AboutUsDialog openAboutUs={openAboutUs} onClose={() => setOpenAboutUs(false)} />
    </Box>
  );
};

export default MobileProfilePage;
